import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';

import Isvg from 'react-inlinesvg';

import { FlyoutContent, FlyoutFooter } from 'components/Flyout';
import { FlyoutContentContext, FlyoutContext } from 'components/Flyout/FlyoutContext';
import { Button, Form } from 'components/HookForm';
import Message from 'components/Message';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { selectUserInfo } from 'selectors/general';

import { ITEM_AVAILABILITY_STATUS } from '../SkuItems.constants';
import { getItemAvailabilityStatus } from '../SkuMenuData.normalizers';
import { skuItemsGroupMessages } from '../SkuItems.messages';
import useSkuVenuePermissions from '../useSkuVenuePermissions';
import IntegratedPartnerBanner from './IntegratedPartnerBanner';
import ItemSaveErrorBanner from './ItemSaveErrorBanner';
import SkuItemFormFields from './SkuItemFormFields';
import SkuItemModifiersSlot from './SkuItemModifiersSlot';
import { messages } from './SkuEditItemForm.messages';

import SkuEditItemAvailabilityDropdown from './SkuEditItemAvailabilityDropdown';

const { element } = bem('item-form');

const getInitialItemValue = (item, key, fallback = '') => item?.[key] ?? fallback;

const SkuEditItemFormContent = ({
  isLoading,
  categoriesLoading,
  groups,
  subgroups,
  currencyCode,
  locale,
  item,
  itemName,
  payoutRate,
  preselectedCategories,
  menuPriceDescription,
  formKey,
  onSubmit,
  isReadOnly,
  onAddCategory,
  modifierGroups,
  modifierGroupsLoading,
  modifierGroupsLoadFailed,
  onRetryModifierGroups,
  preselectedModifierGroupIds,
  onAddModifierGroup,
  showModifiersSection,
  canEditModifiers,
  saveFailure,
  onDismissSaveFailure,
}) => {
  const flyoutContentRef = useContext(FlyoutContentContext);
  const { closeFlyout } = useContext(FlyoutContext);

  useEffect(() => {
    if (saveFailure) flyoutContentRef?.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
  }, [saveFailure, flyoutContentRef]);
  const { integrationName } = useSkuVenuePermissions();
  const { adminUserId } = useSelector(selectUserInfo);
  const isAdminUser = !!adminUserId;

  const getDefaultValues = () => ({
    itemName: itemName || '',
    itemDescription: getInitialItemValue(item, 'description'),
    categories: preselectedCategories,
    availabilityStatus: getItemAvailabilityStatus(item) || ITEM_AVAILABILITY_STATUS.AVAILABLE,
    menuPrice: getInitialItemValue(item, 'menuPrice'),
    linkToMenu: getInitialItemValue(item, 'linkToMenu'),
    modifierGroupIds: preselectedModifierGroupIds,
  });

  return (
    <Form
      key={formKey}
      isLoading={isLoading || categoriesLoading}
      id="sku-edit-item-form"
      defaultValues={getDefaultValues()}
      useFormOverrides={{ mode: 'onSubmit' }}>
      {({ formState, handleSubmit, watch, setValue }) => {
        const { errors, isSubmitting } = formState;
        const [watchCategories, watchItemName, watchItemDescription, menuPrice, linkToMenu, watchModifierGroupIds] =
          watch(['categories', 'itemName', 'itemDescription', 'menuPrice', 'linkToMenu', 'modifierGroupIds']);
        const selectedCategories = Array.isArray(watchCategories) ? watchCategories : preselectedCategories;

        const onInvalid = () => {
          if (typeof flyoutContentRef?.current?.scrollTo === 'function') {
            flyoutContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
          }
        };

        const showFormErrorBanner = !_isEmpty(errors);

        return (
          <>
            <FlyoutContent>
              {isReadOnly && <IntegratedPartnerBanner integrationName={integrationName} />}
              <When predicate={!isReadOnly && Boolean(saveFailure)}>
                <ItemSaveErrorBanner failure={saveFailure} onDismiss={onDismissSaveFailure} />
              </When>
              <When predicate={!isReadOnly && showFormErrorBanner}>
                <div className={element('form-error-banner')}>
                  <Isvg src={ICONS.error} className={element('form-error-icon')} />
                  <Message message={messages.formSubmitError} />
                </div>
              </When>
              {!isReadOnly && (
                <div className={element('availability-status-wrapper')}>
                  <SkuEditItemAvailabilityDropdown name="availabilityStatus" isPending={payoutRate === null} />
                </div>
              )}
              <SkuItemFormFields
                disabled={isReadOnly}
                showPricingFields={!isReadOnly}
                pricingOptional={isAdminUser}
                messages={messages}
                groups={groups}
                subgroups={subgroups}
                currencyCode={currencyCode}
                categories={selectedCategories}
                onCategoriesChange={val => setValue('categories', val)}
                categoriesError={errors.categories}
                onAddCategory={onAddCategory}
                watchItemName={watchItemName}
                watchItemDescription={watchItemDescription}
                menuPriceDescription={menuPriceDescription}
                isEditMode
                itemNameProps={{ defaultValue: itemName || '' }}
                menuPriceProps={{ defaultValue: getInitialItemValue(item, 'menuPrice') }}
                linkToMenuProps={{ defaultValue: getInitialItemValue(item, 'linkToMenu') }}
                priceVerificationSlot={
                  <>
                    <div className={element('payout-rate')}>
                      <span className={element('payout-label-wrapper')}>
                        <Message as="div" className={element('payout-label')} message={skuItemsGroupMessages.payout} />
                        <TooltipToggle className={element('tooltip-toggle')} name="sku-edit-form-payout-tooltip">
                          <Isvg src={ICONS.infoGreyLight} />
                        </TooltipToggle>
                        <TooltipContent hoverable name="sku-edit-form-payout-tooltip" place="right">
                          <Message
                            message={skuItemsGroupMessages.payoutTooltip}
                            values={{
                              a: text => (
                                <a
                                  href={makePath.partnerTaxDefinition(locale)}
                                  target="_blank"
                                  rel="noopener noreferrer">
                                  {text}
                                </a>
                              ),
                            }}
                          />
                        </TooltipContent>
                      </span>
                      {payoutRate === null ? (
                        <div className={element('payout-text')}>––</div>
                      ) : (
                        <Message
                          as="div"
                          className={element('payout-text')}
                          format="currency"
                          currency={currencyCode}
                          value={payoutRate}
                        />
                      )}
                    </div>
                    {!isReadOnly && (
                      <div className={element('info-notice')}>
                        <div className={element('info-content')}>
                          <Isvg src={ICONS.infoBrand} className="info-icon" />
                          <Message message={messages.priceVerificationInfo} />
                        </div>
                      </div>
                    )}
                  </>
                }
                modifiersSlot={
                  showModifiersSection ? (
                    <SkuItemModifiersSlot
                      groups={modifierGroups}
                      value={watchModifierGroupIds}
                      onChange={val => setValue('modifierGroupIds', val)}
                      onAddModifierGroup={canEditModifiers ? onAddModifierGroup : null}
                      isLoading={modifierGroupsLoading}
                      loadFailed={modifierGroupsLoadFailed}
                      onRetry={onRetryModifierGroups}
                      failed={Boolean(saveFailure?.modifiersFailed)}
                      readOnly={!canEditModifiers}
                    />
                  ) : null
                }
              />
            </FlyoutContent>

            <FlyoutFooter>
              {isReadOnly ? (
                <Button styled="primary" className={element('submit-button')} onClick={closeFlyout} type="button">
                  <Message message={messages.closeButton} />
                </Button>
              ) : (
                <Button
                  styled="primary"
                  className={element('submit-button')}
                  onClick={handleSubmit(onSubmit, onInvalid)}
                  waiting={isSubmitting}
                  aria-busy={isSubmitting}
                  disabled={
                    !watchItemName?.trim() ||
                    !watchItemDescription ||
                    !selectedCategories?.length ||
                    !!menuPrice !== !!linkToMenu
                  }>
                  <Message message={isSubmitting ? messages.submittingButton : messages.submitButton} />
                </Button>
              )}
            </FlyoutFooter>
          </>
        );
      }}
    </Form>
  );
};

SkuEditItemFormContent.propTypes = {
  isLoading: PropTypes.bool,
  categoriesLoading: PropTypes.bool,
  groups: PropTypes.arrayOf(PropTypes.shape({})),
  subgroups: PropTypes.arrayOf(PropTypes.shape({})),
  currencyCode: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  item: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    menuPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    linkToMenu: PropTypes.string,
    enabled: PropTypes.bool,
    soldOut: PropTypes.bool,
    published: PropTypes.bool,
    partnerState: PropTypes.string,
  }),
  itemName: PropTypes.string,
  payoutRate: PropTypes.number,
  preselectedCategories: PropTypes.arrayOf(PropTypes.shape({})),
  menuPriceDescription: intlMessage.isRequired,
  formKey: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  onAddCategory: PropTypes.func,
  modifierGroups: PropTypes.arrayOf(PropTypes.shape({})),
  modifierGroupsLoading: PropTypes.bool,
  modifierGroupsLoadFailed: PropTypes.bool,
  onRetryModifierGroups: PropTypes.func,
  saveFailure: PropTypes.shape({
    status: PropTypes.string,
    categoriesFailed: PropTypes.bool,
    modifiersFailed: PropTypes.bool,
  }),
  onDismissSaveFailure: PropTypes.func,
  preselectedModifierGroupIds: PropTypes.arrayOf(PropTypes.string),
  onAddModifierGroup: PropTypes.func,
  showModifiersSection: PropTypes.bool,
  canEditModifiers: PropTypes.bool,
};

SkuEditItemFormContent.defaultProps = {
  isLoading: false,
  categoriesLoading: false,
  groups: [],
  subgroups: [],
  item: null,
  itemName: '',
  payoutRate: null,
  preselectedCategories: [],
  onAddCategory: null,
  modifierGroups: [],
  modifierGroupsLoading: false,
  modifierGroupsLoadFailed: false,
  onRetryModifierGroups: null,
  saveFailure: null,
  onDismissSaveFailure: null,
  preselectedModifierGroupIds: [],
  onAddModifierGroup: null,
  showModifiersSection: false,
  canEditModifiers: false,
};

export default SkuEditItemFormContent;
